import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  hatirlatmaListe, hatirlatmaSes, hatirlatmaSabitSes, hatirlatmaSesli, hatirlatmaCevap,
  hatirlatmaKarar, hatirlatmaEkle, hatirlatmaGuncelle, hatirlatmaGeriAl, hatirlatmaSil,
  hatirlatmaBildirimAnahtari, hatirlatmaAbone, hatirlatmaAboneSil, hatirlatmaTestBildirim,
} from '../services/api';
import {
  sesKilidiniAc, sesCal, sesiDurdur, kayitBaslat, kayitDestekleniyor, bip, sabitSesCal,
  bildirimDestekleniyor, iPhoneMu, anaEkrandaMi, mevcutAbonelik, aboneOl, aboneliktenCik,
} from '../asistanSes';
import './KisiselAsistan.css';

// KİŞİSEL ASİSTAN — sesli hatırlatıcı (kullanıcı talebi: 2026-09-18).
//
// Amaç (kullanıcının sözleriyle): "patron telefonunda sesli kayıt oluştursun, zamanı gelince
// bildirim alsın, bu ekrana gelsin, hatırlatma mesajını telesekreterden dinler gibi dinlesin."
//
// Ekran %99 telefonda kullanılacak: büyük mikrofon, büyük oynat düğmeleri, tek sütun.
//
// TELESEKRETER DİYALOĞU: zamanı gelmiş mesaj okununca sekreter "Patron, ne yapalım? Bu konuyu
// kapatalım mı, yoksa erteleyelim mi?" diye sorar, mikrofon kendiliğinden açılır. Serbest cevabı
// ("kapat", "devam et", "3 saat sonra hatırlat") yapay zekâ yorumlar; aynı seçenekler düğme olarak da var.
// Sekreterin kalıp cümleleri SABİT ses dosyasıdır — her seferinde yeniden seslendirilmez.

const bugunIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const GUNLER = ['Paz', 'Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt'];
const AYLAR = ['Oca', 'Şub', 'Mar', 'Nis', 'May', 'Haz', 'Tem', 'Ağu', 'Eyl', 'Eki', 'Kas', 'Ara'];

// "Bugün", "Yarın", "3 gün gecikti", "25 Eyl Per"
function gunEtiketi(iso, onayli) {
  const t = new Date(String(iso).slice(0, 10) + 'T00:00:00');
  const b = new Date(bugunIso() + 'T00:00:00');
  const fark = Math.round((t - b) / 86400000);
  if (fark === 0) return 'Bugün';
  if (fark === 1) return 'Yarın';
  if (fark < 0 && !onayli) return fark === -1 ? 'Dün · gecikti' : `${-fark} gün gecikti`;
  return `${t.getDate()} ${AYLAR[t.getMonth()]} ${GUNLER[t.getDay()]}`;
}

const saatYaz = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const gun = d.toDateString() === new Date().toDateString() ? '' : `${d.getDate()} ${AYLAR[d.getMonth()]} `;
  return `${gun}${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

const hataMetni = (err, varsayilan) => err?.response?.data?.hata || err?.message || varsayilan;

export default function KisiselAsistan() {
  const [params, setParams] = useSearchParams();

  const [durum, setDurum] = useState('bekleyen');
  const [ara, setAra] = useState('');
  const [baslangic, setBaslangic] = useState('');
  const [bitis, setBitis] = useState('');
  const [filtreAcik, setFiltreAcik] = useState(false);

  const [kayitlar, setKayitlar] = useState([]);
  const [yeniMesaj, setYeniMesaj] = useState(0);
  const [yukleniyor, setYukleniyor] = useState(false);
  const [hata, setHata] = useState('');
  const [bilgi, setBilgi] = useState('');

  const [acikKart, setAcikKart] = useState(null);
  const [oynatici, setOynatici] = useState(null);   // telesekreter penceresi
  const [form, setForm] = useState(null);           // yeni/düzenle penceresi
  const [bildirim, setBildirim] = useState('bilinmiyor');

  const listele = useCallback(async () => {
    setYukleniyor(true);
    try {
      const p = { durum };
      if (ara.trim()) p.ara = ara.trim();
      if (baslangic) p.baslangic = baslangic;
      if (bitis) p.bitis = bitis;
      const { data } = await hatirlatmaListe(p);
      setKayitlar(data.kayitlar);
      setYeniMesaj(data.yeniMesaj);
      setHata('');
    } catch (err) {
      setHata(hataMetni(err, 'Hatırlatmalar yüklenemedi.'));
    } finally {
      setYukleniyor(false);
    }
  }, [durum, ara, baslangic, bitis]);

  useEffect(() => { listele(); }, [listele]);

  // Bildirimden gelindiyse (?ac=ID) o kayıt telesekreterde açılır. Ses, iPhone kısıtı yüzünden
  // tek dokunuşla başlar ("Dinle" düğmesi) — tarayıcı dokunuşsuz sesi engelliyor.
  useEffect(() => {
    const ac = Number(params.get('ac'));
    if (!ac) return;
    (async () => {
      try {
        const { data } = await hatirlatmaListe({ durum: 'tumu' });
        const k = data.kayitlar.find(x => x.id === ac);
        if (k) setOynatici({ kayit: k, sira: [], asama: 'hazir' });
      } catch { /* liste zaten hata gösterir */ }
      params.delete('ac');
      setParams(params, { replace: true });
    })();
  }, [params, setParams]);

  // --- Bildirim durumu ---
  const bildirimDurumuOku = useCallback(async () => {
    if (!bildirimDestekleniyor()) {
      setBildirim(iPhoneMu() && !anaEkrandaMi() ? 'iphone' : 'desteklenmiyor');
      return;
    }
    if (Notification.permission === 'denied') { setBildirim('reddedildi'); return; }
    try {
      const abonelik = await mevcutAbonelik();
      if (abonelik && Notification.permission === 'granted') {
        // Abonelik her ekran açılışında sunucuya tazelenir (şube/kullanıcı değişmiş olabilir).
        const j = abonelik.toJSON();
        hatirlatmaAbone({ endpoint: j.endpoint, p256dh: j.keys.p256dh, auth: j.keys.auth }).catch(() => {});
        setBildirim('acik');
      } else {
        setBildirim('kapali');
      }
    } catch {
      setBildirim('kapali');
    }
  }, []);

  useEffect(() => { bildirimDurumuOku(); }, [bildirimDurumuOku]);

  const bildirimAc = async () => {
    setHata('');
    try {
      const { data } = await hatirlatmaBildirimAnahtari();
      const abonelik = await aboneOl(data.anahtar);
      await hatirlatmaAbone(abonelik);
      setBildirim('acik');
      setBilgi('Bildirimler açıldı. Deneme bildirimi gönderiliyor…');
      await hatirlatmaTestBildirim();
    } catch (err) {
      setHata(hataMetni(err, 'Bildirimler açılamadı.'));
      bildirimDurumuOku();
    }
  };

  const bildirimKapat = async () => {
    try {
      const endpoint = await aboneliktenCik();
      if (endpoint) await hatirlatmaAboneSil(endpoint);
      setBildirim('kapali');
      setBilgi('Bu cihazda bildirimler kapatıldı.');
    } catch (err) {
      setHata(hataMetni(err, 'Bildirimler kapatılamadı.'));
    }
  };

  const testBildirimi = async () => {
    setHata('');
    try {
      const { data } = await hatirlatmaTestBildirim();
      setBilgi(data.gonderilen > 0
        ? 'Deneme bildirimi gönderildi — birkaç saniye içinde gelmeli.'
        : 'Bildirim gönderilemedi: bu cihazın aboneliği bulunamadı. Bildirimleri kapatıp yeniden açın.');
    } catch (err) {
      setHata(hataMetni(err, 'Deneme bildirimi gönderilemedi.'));
    }
  };

  // --- Telesekreter ---
  // Ekranda hangi sekme açık olursa olsun, zamanı gelmiş bütün mesajlar sırayla çalınır.
  const hepsiniDinle = async () => {
    sesKilidiniAc();
    let bekleyenler = kayitlar.filter(k => k.zamaniGeldi);
    if (durum !== 'bekleyen' || ara || baslangic || bitis) {
      try {
        const { data } = await hatirlatmaListe({ durum: 'bekleyen' });
        bekleyenler = data.kayitlar.filter(k => k.zamaniGeldi);
      } catch (err) {
        setHata(hataMetni(err, 'Mesajlar alınamadı.'));
        return;
      }
    }
    if (bekleyenler.length === 0) return;
    const [ilk, ...kalan] = bekleyenler;
    setOynatici({ kayit: ilk, sira: kalan, asama: 'basla', no: 1, toplam: bekleyenler.length });
  };

  const dinle = (kayit) => {
    sesKilidiniAc();
    setOynatici({ kayit, sira: [], asama: 'basla' });
  };

  const oynaticiKapandi = () => {
    setOynatici(null);
    listele();
  };

  // --- Yeni kayıt / düzenleme ---
  const yeniSesli = () => {
    sesKilidiniAc();
    setForm({ asama: 'kayit' });
  };

  const yeniYazili = () => {
    sesKilidiniAc();
    setForm({ asama: 'taslak', tarih: bugunIso(), konu: '' });
  };

  const duzenle = (k) => {
    sesKilidiniAc();
    setForm({ asama: 'taslak', id: k.id, tarih: String(k.tarih).slice(0, 10), konu: k.konu, orijinalKonu: k.konu });
  };

  const formKaydedildi = (mesaj, yeniKayit) => {
    setForm(null);
    setBilgi(mesaj);
    listele();
    // "Patron, mesajını kaydettim…" — pencere kapandıktan SONRA çalınır; kapanan pencere
    // kendi sesini durdurduğu için hemen çalınırsa kesilirdi.
    if (yeniKayit) setTimeout(() => sabitCal('kayit-tamam'), 80);
  };

  // --- Kart işlemleri ---
  const karar = async (k, secim, saat) => {
    try {
      await hatirlatmaKarar(k.id, secim, saat);
      setBilgi(secim === 'yapildi' ? 'Yapıldı olarak işaretlendi.' : `${saat} saat sonra tekrar hatırlatılacak.`);
      listele();
    } catch (err) {
      setHata(hataMetni(err, 'İşlem yapılamadı.'));
    }
  };

  const geriAl = async (k) => {
    try {
      await hatirlatmaGeriAl(k.id);
      setBilgi('Tekrar hatırlatılacak.');
      listele();
    } catch (err) {
      setHata(hataMetni(err, 'İşlem yapılamadı.'));
    }
  };

  const sil = async (k) => {
    if (!window.confirm(`"${k.konu}" silinsin mi?`)) return;
    try {
      await hatirlatmaSil(k.id);
      setBilgi('Hatırlatma silindi.');
      listele();
    } catch (err) {
      setHata(hataMetni(err, 'Silinemedi.'));
    }
  };

  useEffect(() => {
    if (!bilgi) return undefined;
    const t = setTimeout(() => setBilgi(''), 4000);
    return () => clearTimeout(t);
  }, [bilgi]);

  return (
    <div className="ka">
      {/* TELESEKRETER — "3 yeni mesajınız var" */}
      <section className={`ka-telesekreter${yeniMesaj > 0 ? ' dolu' : ''}`}>
        <div className="ka-ts-sayac">
          <span className="ka-ts-sayi">{yeniMesaj}</span>
          <span className="ka-ts-yazi">
            {yeniMesaj > 0 ? (yeniMesaj === 1 ? 'yeni mesajınız var' : 'yeni mesajınız var') : 'Yeni mesajınız yok'}
          </span>
        </div>
        {yeniMesaj > 0 && (
          <button className="ka-ts-dinle" onClick={hepsiniDinle} aria-label="Mesajları dinle">
            <span className="ka-oynat-ikon">▶</span> Dinle
          </button>
        )}
      </section>

      {/* BÜYÜK MİKROFON */}
      <section className="ka-mikrofon-alani">
        {kayitDestekleniyor() ? (
          <button className="ka-mikrofon" onClick={yeniSesli} aria-label="Sesli hatırlatma kaydet">
            <MikrofonIkon />
          </button>
        ) : (
          <p className="ka-uyari">Bu tarayıcı ses kaydını desteklemiyor.</p>
        )}
        <div className="ka-mikrofon-yazi">Dokunun ve hatırlatmayı söyleyin</div>
        <div className="ka-mikrofon-ornek">"Perşembe günü Ahmet Bey'i ara, cihaz teklifini sor"</div>
        <button className="ka-yazili" onClick={yeniYazili}>✎ Yazarak ekle</button>
      </section>

      <BildirimSeridi durum={bildirim} ac={bildirimAc} kapat={bildirimKapat} test={testBildirimi} />

      {hata && <div className="ka-hata">{hata}</div>}
      {bilgi && <div className="ka-bilgi">{bilgi}</div>}

      {/* SÜZGEÇLER */}
      <section className="ka-suzgec">
        <div className="ka-sekmeler" role="tablist">
          {[['bekleyen', 'Bekleyen'], ['onayli', 'Yapılan'], ['tumu', 'Tümü']].map(([v, ad]) => (
            <button key={v} role="tab" aria-selected={durum === v}
                    className={durum === v ? 'secili' : ''} onClick={() => setDurum(v)}>{ad}</button>
          ))}
          <button className={`ka-ara-dugme${filtreAcik ? ' secili' : ''}`} onClick={() => setFiltreAcik(!filtreAcik)}
                  aria-label="Arama ve tarih süzgeci">🔍</button>
        </div>
        {filtreAcik && (
          <div className="ka-filtre">
            <input type="search" placeholder="Konuda ara…" value={ara} onChange={e => setAra(e.target.value)} />
            <div className="ka-filtre-tarih">
              <label>Başlangıç<input type="date" value={baslangic} onChange={e => setBaslangic(e.target.value)} /></label>
              <label>Bitiş<input type="date" value={bitis} onChange={e => setBitis(e.target.value)} /></label>
            </div>
            {(ara || baslangic || bitis) && (
              <button className="ka-temizle" onClick={() => { setAra(''); setBaslangic(''); setBitis(''); }}>Süzgeci temizle</button>
            )}
          </div>
        )}
      </section>

      {/* LİSTE */}
      <section className="ka-liste">
        {yukleniyor && kayitlar.length === 0 && <div className="ka-bos">Yükleniyor…</div>}
        {!yukleniyor && kayitlar.length === 0 && (
          <div className="ka-bos">
            {durum === 'bekleyen' ? 'Bekleyen hatırlatma yok.' : 'Kayıt bulunamadı.'}
          </div>
        )}
        {kayitlar.map(k => (
          <article key={k.id}
                   className={`ka-kart${k.zamaniGeldi ? ' zamani-geldi' : ''}${k.onay ? ' onayli' : ''}${acikKart === k.id ? ' acik' : ''}`}>
            <div className="ka-kart-ust">
              <button className="ka-oynat" onClick={() => dinle(k)} aria-label="Dinle">
                <span className="ka-oynat-ikon">▶</span>
              </button>
              <button className="ka-kart-govde" onClick={() => setAcikKart(acikKart === k.id ? null : k.id)}>
                <span className="ka-etiketler">
                  <span className={`ka-gun${k.zamaniGeldi ? ' sicak' : ''}`}>{gunEtiketi(k.tarih, k.onay)}</span>
                  {k.onay && <span className="ka-rozet yapildi">✔ Yapıldı</span>}
                  {k.erteleBitis && <span className="ka-rozet ertelendi">⏰ {saatYaz(k.erteleBitis)}'e kadar</span>}
                </span>
                <span className="ka-konu">{k.konu}</span>
              </button>
            </div>

            {acikKart === k.id && (
              <div className="ka-kart-alt">
                {k.sekreterMetni && <p className="ka-sekreter-metni">"{k.sekreterMetni}"</p>}
                <p className="ka-kart-bilgi">
                  {k.olusturan} · {saatYaz(k.olusturmaZamani)}
                  {k.bildirimSayisi > 0 && ` · ${k.bildirimSayisi} kez bildirildi`}
                  {k.onay && k.onayZamani && ` · yapıldı: ${saatYaz(k.onayZamani)}`}
                </p>
                <div className="ka-kart-dugmeler">
                  {!k.onay ? (
                    <>
                      <button className="ka-dugme yesil" onClick={() => karar(k, 'yapildi')}>✔ Yapıldı</button>
                      <button className="ka-dugme" onClick={() => karar(k, 'ertele', 3)}>⏰ 3 saat sonra</button>
                    </>
                  ) : (
                    <button className="ka-dugme" onClick={() => geriAl(k)}>↺ Tekrar hatırlat</button>
                  )}
                  <button className="ka-dugme" onClick={() => duzenle(k)}>✎ Düzenle</button>
                  <button className="ka-dugme kirmizi" onClick={() => sil(k)}>🗑 Sil</button>
                </div>
              </div>
            )}
          </article>
        ))}
      </section>

      {oynatici && (
        <Telesekreter baslangic={oynatici} kapat={oynaticiKapandi} sayacGuncelle={setYeniMesaj} />
      )}

      {form && <HatirlatmaFormu baslangic={form} kapat={() => setForm(null)} kaydedildi={formKaydedildi} />}
    </div>
  );
}

// ---------------------------------------------------------------------------

function MikrofonIkon() {
  return (
    <svg viewBox="0 0 24 24" width="54" height="54" aria-hidden="true">
      <path fill="currentColor"
            d="M12 15a3.5 3.5 0 0 0 3.5-3.5v-6a3.5 3.5 0 0 0-7 0v6A3.5 3.5 0 0 0 12 15Zm6.25-3.5a.75.75 0 0 0-1.5 0 4.75 4.75 0 0 1-9.5 0 .75.75 0 0 0-1.5 0 6.26 6.26 0 0 0 5.5 6.2V20H9a.75.75 0 0 0 0 1.5h6a.75.75 0 0 0 0-1.5h-2.25v-2.3a6.26 6.26 0 0 0 5.5-6.2Z" />
    </svg>
  );
}

function BildirimSeridi({ durum, ac, kapat, test }) {
  if (durum === 'bilinmiyor') return null;
  if (durum === 'acik') {
    return (
      <div className="ka-bildirim acik">
        <span>🔔 Bu cihazda bildirimler açık</span>
        <span className="ka-bildirim-dugmeler">
          <button onClick={test}>Dene</button>
          <button onClick={kapat}>Kapat</button>
        </span>
      </div>
    );
  }
  if (durum === 'kapali') {
    return (
      <button className="ka-bildirim kapali" onClick={ac}>
        🔔 Hatırlatma bildirimlerini aç
        <small>Zamanı gelince telefonunuza bildirim gelsin</small>
      </button>
    );
  }
  if (durum === 'iphone') {
    return (
      <div className="ka-bildirim uyari">
        📱 iPhone'da bildirim almak için: Safari'de <b>Paylaş</b> → <b>Ana Ekrana Ekle</b>'ye dokunun,
        sonra uygulamayı ana ekrandaki simgeden açıp bu ekrana gelin.
      </div>
    );
  }
  if (durum === 'reddedildi') {
    return (
      <div className="ka-bildirim uyari">
        🔕 Bu sitenin bildirimleri engellenmiş. Tarayıcı ayarlarından bu siteye bildirim izni verin.
      </div>
    );
  }
  return <div className="ka-bildirim uyari">Bu tarayıcı bildirimleri desteklemiyor.</div>;
}

// ---------------------------------------------------------------------------
// TELESEKRETER: mesajı sekreterin üslubuyla okur, ardından mesajın durumuna göre SABİT bir
// cümleyle bitirir (kullanıcı talebi: 2026-09-18):
//   zamanı gelmiş + onaysız → "Patron, ne yapalım? Bu konuyu kapatalım mı, yoksa
//                              erteleyelim mi?" → dinler → AI karar verir (kapat/devam/ertele)
//   zamanı gelmemiş         → "Patron, bu mesajı sana zamanı geldiğinde hatırlatacağım."
//   onaylı eski mesaj       → "Patron, bu mesaj onaylanmış eski bir mesajdır."

// Kararın sabit cevap sesi ve ekrandaki yazısı.
const CEVAP_YAZISI = {
  yapildi: 'Tamam patron, bu konuyu kapattım.',
  devam: 'Tamam patron, hatırlatmaya devam edeceğim.',
  ertele: 'Tamam patron, erteledim. Zamanı gelince tekrar hatırlatacağım.',
  anlasilmadi: 'Kusura bakma patron, anlayamadım. Aşağıdaki düğmelerden seçebilirsin.',
};

const sabitCal = (anahtar) => sabitSesCal(anahtar, hatirlatmaSabitSes).catch(() => {});

function Telesekreter({ baslangic, kapat, sayacGuncelle }) {
  const [kayit, setKayit] = useState(baslangic.kayit);
  const [sira, setSira] = useState(baslangic.sira || []);
  const [no, setNo] = useState(baslangic.no || 1);
  const toplam = baslangic.toplam || 1;
  // hazir|basla|okunuyor|soruyor|dinliyor|dusunuyor|cevap|secim|erken|onayli|bitti
  const [asama, setAsama] = useState(baslangic.asama);
  const [seviye, setSeviye] = useState(0);
  const [cevap, setCevap] = useState(null);
  const [hata, setHata] = useState('');

  const iptal = useRef(false);
  const kayitRef = useRef(null);

  useEffect(() => {
    iptal.current = false;
    return () => {
      iptal.current = true;
      sesiDurdur();
      if (kayitRef.current) kayitRef.current.durdur();
    };
  }, []);

  const sonraki = useCallback(() => {
    if (sira.length === 0) { setAsama('bitti'); return; }
    const [ilk, ...kalan] = sira;
    setKayit(ilk);
    setSira(kalan);
    setNo(n => n + 1);
    setCevap(null);
    setHata('');
    setAsama('basla');
  }, [sira]);

  const kararUygulandi = useCallback((data) => {
    if (typeof data.yeniMesaj === 'number') sayacGuncelle(data.yeniMesaj);
    setKayit(k => ({
      ...k,
      onay: data.karar === 'yapildi' ? true : k.onay,
      erteleBitis: data.erteleBitis ?? null,
    }));
  }, [sayacGuncelle]);

  // Karar sonrası: sabit cevap sesi, sonra sıradaki mesaja geçiş.
  const kararSonrasi = useCallback(async (karar, sesMetni, erteleBitis) => {
    setCevap({ karar, sesMetni, erteleBitis });
    setAsama(karar === 'anlasilmadi' ? 'secim' : 'cevap');
    await sabitCal(`cevap-${karar}`);
    if (iptal.current || karar === 'anlasilmadi') return;
    setTimeout(() => { if (!iptal.current) sonraki(); }, 700);
  }, [sonraki]);

  const dinleVeCevapla = useCallback(async () => {
    setHata('');
    setAsama('dinliyor');
    let sonuc;
    try {
      const k = await kayitBaslat({ sessizlikMs: 1500, baslamazsaMs: 7000, azamiMs: 20000, seviye: setSeviye });
      kayitRef.current = k;
      sonuc = await k.bitti;
      kayitRef.current = null;
    } catch {
      setHata('Mikrofona erişilemedi. Aşağıdaki düğmelerden seçebilirsiniz.');
      setAsama('secim');
      return;
    }
    if (iptal.current) return;
    if (!sonuc.konusuldu) { setAsama('secim'); return; }

    setAsama('dusunuyor');
    try {
      const { data } = await hatirlatmaCevap(kayit.id, sonuc.blob, sonuc.dosyaAdi);
      if (iptal.current) return;
      kararUygulandi(data);
      await kararSonrasi(data.karar, data.sesMetni, data.erteleBitis);
    } catch (err) {
      setHata(hataMetni(err, 'Cevap işlenemedi.'));
      setAsama('secim');
    }
  }, [kayit, kararUygulandi, kararSonrasi]);

  // Mesaj → duruma göre sabit kapanış cümlesi → (zamanı gelmişse) dinleme.
  const oynat = useCallback(async () => {
    setHata('');
    setCevap(null);
    try {
      if (kayit.sesVar) {
        setAsama('okunuyor');
        const { data } = await hatirlatmaSes(kayit.id);
        if (iptal.current) return;
        await sesCal(data);
      }
      if (iptal.current) return;

      if (kayit.onay) {
        setAsama('onayli');
        await sabitCal('onayli');
        return;
      }
      if (!kayit.zamaniGeldi) {
        setAsama('erken');
        await sabitCal('erken');
        return;
      }

      setAsama('soruyor');
      await sabitCal('soru');
      if (iptal.current) return;
      await dinleVeCevapla();
    } catch (err) {
      if (iptal.current) return;
      setHata(hataMetni(err, 'Ses çalınamadı.'));
      setAsama('secim');
    }
  }, [kayit, dinleVeCevapla]);

  useEffect(() => {
    if (asama === 'basla') oynat();
  }, [asama, oynat]);

  const dugmeKarari = async (secim, saat) => {
    sesKilidiniAc();
    sesiDurdur();
    if (kayitRef.current) kayitRef.current.durdur();
    try {
      let erteleBitis = null;
      if (secim !== 'devam' || kayit.erteleBitis) {
        const { data } = await hatirlatmaKarar(kayit.id, secim, saat);
        kararUygulandi({ ...data, karar: secim });
        erteleBitis = data.erteleBitis;
      }
      await kararSonrasi(secim, null, erteleBitis);
    } catch (err) {
      setHata(hataMetni(err, 'İşlem yapılamadı.'));
    }
  };

  const tekrarDinle = () => { sesKilidiniAc(); setAsama('basla'); };
  const tekrarKonus = () => { sesKilidiniAc(); dinleVeCevapla(); };

  const durumYazisi = {
    hazir: 'Mesajınız hazır',
    basla: 'Hazırlanıyor…',
    okunuyor: 'Mesajınız okunuyor…',
    soruyor: 'Kapatalım mı, erteleyelim mi?',
    dinliyor: 'Sizi dinliyorum… konuşun',
    dusunuyor: 'Anlamaya çalışıyorum…',
    cevap: cevap ? CEVAP_YAZISI[cevap.karar] + (cevap.erteleBitis ? ` (${saatYaz(cevap.erteleBitis)})` : '') : '',
    secim: cevap?.karar === 'anlasilmadi' ? CEVAP_YAZISI.anlasilmadi : 'Ne yapalım?',
    erken: 'Zamanı geldiğinde hatırlatılacak.',
    onayli: 'Bu mesaj onaylanmış eski bir mesaj.',
    bitti: 'Tüm mesajlar dinlendi',
  }[asama];

  // Karar düğmeleri onaysız her mesajda görünür (zamanı gelmemiş bir işi erken kapatmak da mümkün).
  const kararButonlari = !kayit.onay
    && ['secim', 'dinliyor', 'dusunuyor', 'soruyor', 'okunuyor', 'hazir', 'erken'].includes(asama);

  return (
    <div className="ka-perde" role="dialog" aria-modal="true">
      <div className="ka-ts-pencere">
        <div className="ka-ts-baslik">
          <span>📼 Telesekreter{toplam > 1 && asama !== 'bitti' ? ` · ${no}/${toplam}` : ''}</span>
          <button className="ka-kapat" onClick={kapat} aria-label="Kapat">✕</button>
        </div>

        {asama === 'bitti' ? (
          <div className="ka-ts-govde">
            <div className="ka-ts-durum">✔ Tüm mesajlar dinlendi</div>
            <button className="ka-buyuk-dugme" onClick={kapat}>Kapat</button>
          </div>
        ) : (
          <div className="ka-ts-govde">
            <div className="ka-ts-gun">{gunEtiketi(kayit.tarih, kayit.onay)}</div>
            <div className="ka-ts-konu">{kayit.konu}</div>

            <div className={`ka-ts-gorsel ${asama}`} style={{ '--seviye': seviye }}>
              {asama === 'hazir' ? (
                <button className="ka-ts-buyuk-oynat" onClick={tekrarDinle} aria-label="Dinle">
                  <span className="ka-oynat-ikon">▶</span>
                </button>
              ) : asama === 'dinliyor' ? (
                <button className="ka-ts-dinliyor" onClick={() => kayitRef.current?.durdur()} aria-label="Konuşmayı bitir">
                  <MikrofonIkon />
                </button>
              ) : (
                <div className="ka-ts-dalga"><i /><i /><i /><i /><i /></div>
              )}
            </div>

            <div className="ka-ts-durum">{durumYazisi}</div>
            {asama === 'dinliyor' && <div className="ka-ts-ipucu">"Kapat", "devam et", "3 saat sonra hatırlat", "yarın sabah söyle"…</div>}
            {cevap?.sesMetni && asama === 'cevap' && <div className="ka-ts-ipucu">Siz: "{cevap.sesMetni}"</div>}
            {hata && <div className="ka-hata">{hata}</div>}

            {kararButonlari && (
              <div className="ka-ts-kararlar">
                <button className="ka-buyuk-dugme yesil" onClick={() => dugmeKarari('yapildi')}>✔ Kapat (yapıldı)</button>
                <button className="ka-buyuk-dugme" onClick={() => dugmeKarari('ertele', 3)}>⏰ 3 saat ertele</button>
                {asama !== 'erken' && (
                  <button className="ka-buyuk-dugme" onClick={() => dugmeKarari('devam')}>↻ Hatırlatmaya devam et</button>
                )}
              </div>
            )}

            {['secim', 'cevap', 'erken', 'onayli'].includes(asama) && (
              <div className="ka-ts-alt">
                <button className="ka-dugme" onClick={tekrarDinle}>▶ Tekrar dinle</button>
                {asama === 'secim' && !kayit.onay && kayit.zamaniGeldi &&
                  <button className="ka-dugme" onClick={tekrarKonus}>🎤 Sesli cevap ver</button>}
                {sira.length > 0 && <button className="ka-dugme" onClick={sonraki}>Sonraki ›</button>}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// YENİ / DÜZENLE: sesli kayıt → yapay zekâ formu doldurur → "Kaydet" (kullanıcı kararı:
// önce göster, ben onaylayayım).

function HatirlatmaFormu({ baslangic, kapat, kaydedildi }) {
  const [asama, setAsama] = useState(baslangic.asama);   // kayit|isleniyor|taslak|kaydediliyor
  const [tarih, setTarih] = useState(baslangic.tarih || bugunIso());
  const [konu, setKonu] = useState(baslangic.konu || '');
  const [taslak, setTaslak] = useState(null);             // sesli kayıttan gelen AI çıktısı
  const [seviye, setSeviye] = useState(0);
  const [hata, setHata] = useState('');
  const kayitRef = useRef(null);
  const iptal = useRef(false);

  useEffect(() => {
    iptal.current = false;
    return () => {
      iptal.current = true;
      sesiDurdur();
      if (kayitRef.current) kayitRef.current.durdur();
    };
  }, []);

  const kaydet = useCallback(async () => {
    setHata('');
    // Telesekreter gibi: "Patron, lütfen mesajınızı söyleyin." + bip, sonra kayıt.
    setAsama('karsilama');
    await sabitCal('kayit-basla');
    if (iptal.current) return;
    await bip();
    if (iptal.current) return;
    setAsama('kayit');
    let sonuc;
    try {
      const k = await kayitBaslat({ sessizlikMs: 2500, baslamazsaMs: 10000, azamiMs: 90000, seviye: setSeviye });
      kayitRef.current = k;
      sonuc = await k.bitti;
      kayitRef.current = null;
    } catch {
      setHata('Mikrofona erişilemedi. Tarayıcıya mikrofon izni verin ya da yazarak ekleyin.');
      setAsama('taslak');
      return;
    }
    if (iptal.current) return;
    if (!sonuc.konusuldu) {
      setHata('Ses duyulmadı. Tekrar deneyin ya da yazarak ekleyin.');
      setAsama('taslak');
      return;
    }

    setAsama('isleniyor');
    try {
      const { data } = await hatirlatmaSesli(sonuc.blob, sonuc.dosyaAdi);
      if (iptal.current) return;
      setTaslak(data);
      setKonu(data.konu || '');
      if (data.tarih) setTarih(data.tarih);
      else setHata('Tarih anlaşılamadı, lütfen seçin.');
      setAsama('taslak');
      // Sekreter nasıl okuyacağını hemen seslendirir.
      if (data.ses) sesCal(data.ses).catch(() => {});
    } catch (err) {
      setHata(hataMetni(err, 'Ses işlenemedi.'));
      setAsama('taslak');
    }
  }, []);

  const basladi = useRef(false);
  useEffect(() => {
    if (baslangic.asama === 'kayit' && !basladi.current) { basladi.current = true; kaydet(); }
  }, [baslangic.asama, kaydet]);

  const kaydetDugmesi = async () => {
    setHata('');
    if (!konu.trim()) { setHata('Hatırlatılacak konuyu yazınız.'); return; }
    if (!tarih) { setHata('Hatırlatma tarihini seçiniz.'); return; }
    sesiDurdur();
    setAsama('kaydediliyor');

    // Konu, yapay zekânın yazdığıyla aynıysa hazır ses kullanılır; değiştiyse sunucu yeniden üretir.
    const hazir = taslak && konu.trim() === (taslak.konu || '').trim();
    const veri = {
      tarih,
      konu: konu.trim(),
      sekreterMetni: hazir ? taslak.sekreterMetni : null,
      ses: hazir ? taslak.ses : null,
      sesMetni: taslak?.sesMetni || null,
    };
    try {
      const { data } = baslangic.id
        ? await hatirlatmaGuncelle(baslangic.id, veri)
        : await hatirlatmaEkle(veri);
      kaydedildi(data.uyari || (baslangic.id ? 'Hatırlatma güncellendi.' : 'Hatırlatma kaydedildi.'), !baslangic.id);
    } catch (err) {
      setHata(hataMetni(err, 'Kaydedilemedi.'));
      setAsama('taslak');
    }
  };

  return (
    <div className="ka-perde" role="dialog" aria-modal="true">
      <div className="ka-ts-pencere">
        <div className="ka-ts-baslik">
          <span>{baslangic.id ? '✎ Hatırlatmayı düzenle' : '🎙️ Yeni hatırlatma'}</span>
          <button className="ka-kapat" onClick={kapat} aria-label="Kapat">✕</button>
        </div>

        {asama === 'karsilama' && (
          <div className="ka-ts-govde">
            <div className="ka-ts-gorsel okunuyor"><div className="ka-ts-dalga"><i /><i /><i /><i /><i /></div></div>
            <div className="ka-ts-durum">Patron, lütfen mesajınızı söyleyin…</div>
            <div className="ka-ts-ipucu">Bip sesinden sonra konuşun.</div>
          </div>
        )}

        {asama === 'kayit' && (
          <div className="ka-ts-govde">
            <div className="ka-ts-gorsel dinliyor" style={{ '--seviye': seviye }}>
              <button className="ka-ts-dinliyor" onClick={() => kayitRef.current?.durdur()} aria-label="Kaydı bitir">
                <MikrofonIkon />
              </button>
            </div>
            <div className="ka-ts-durum">Dinliyorum… konuşun</div>
            <div className="ka-ts-ipucu">Ne zaman ve neyi hatırlatayım? Bitince susmanız yeterli ya da mikrofona dokunun.</div>
          </div>
        )}

        {asama === 'isleniyor' && (
          <div className="ka-ts-govde">
            <div className="ka-ts-gorsel dusunuyor"><div className="ka-ts-dalga"><i /><i /><i /><i /><i /></div></div>
            <div className="ka-ts-durum">Hatırlatmanız hazırlanıyor…</div>
          </div>
        )}

        {(asama === 'taslak' || asama === 'kaydediliyor') && (
          <div className="ka-ts-govde ka-form">
            {taslak?.sesMetni && <div className="ka-ts-ipucu">Siz: "{taslak.sesMetni}"</div>}

            <label className="ka-alan">
              <span>Hatırlatma tarihi</span>
              <input type="date" value={tarih} min={baslangic.id ? undefined : bugunIso()}
                     onChange={e => setTarih(e.target.value)} />
            </label>

            <label className="ka-alan">
              <span>Konu</span>
              <textarea rows={3} value={konu} maxLength={1000} onChange={e => setKonu(e.target.value)}
                        placeholder="Neyi hatırlatayım?" />
            </label>

            {taslak?.sekreterMetni && konu.trim() === (taslak.konu || '').trim() && (
              <div className="ka-onizleme">
                <button className="ka-oynat kucuk" onClick={() => { sesKilidiniAc(); sesCal(taslak.ses).catch(() => {}); }}
                        aria-label="Sekreterin mesajını dinle">
                  <span className="ka-oynat-ikon">▶</span>
                </button>
                <span>"{taslak.sekreterMetni}"</span>
              </div>
            )}

            {hata && <div className="ka-hata">{hata}</div>}

            <button className="ka-buyuk-dugme yesil" onClick={kaydetDugmesi} disabled={asama === 'kaydediliyor'}>
              {asama === 'kaydediliyor' ? 'Kaydediliyor…' : '✔ Kaydet'}
            </button>
            {!baslangic.id && kayitDestekleniyor() && (
              <button className="ka-dugme" onClick={() => { sesKilidiniAc(); setTaslak(null); kaydet(); }}
                      disabled={asama === 'kaydediliyor'}>
                🎤 Yeniden söyle
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
